import { saveDraft, getDraft, deleteDraft, getAllDraftData } from '../Components/functions/Api';
import { pinImagesToIPFS } from '../Components/functions/IPFS';
import { toast } from 'react-toastify';

/**
 * Service for managing property drafts with image upload support
 */
class DraftService {
  
  /**
   * Save a draft with automatic image upload handling
   * @param {string} walletAddress - User's wallet address
   * @param {string|null} propertyId - Existing draft ID or null for new draft
   * @param {object} draftData - Draft data including images
   * @param {number} currentStep - Current step (0-4)
   * @param {boolean} uploadImages - Whether to upload images to Cloudinary
   * @returns {Promise<object>} Save response with property_id
   */
  async saveDraftWithImages(walletAddress, propertyId, draftData, currentStep, uploadImages = false) {
    try {
      let processedDraftData = { ...draftData };
      
      // If images exist and uploadImages is true, upload them to Cloudinary first
      if (uploadImages && draftData.metaDetails?.images && draftData.metaDetails.images.length > 0) {
        // Separate images that need uploading (objects with file property) from IPFS hashes (strings)
        const imagesToUpload = [];
        const ipfsHashes = [];
        
        draftData.metaDetails.images.forEach(img => {
          if (typeof img === 'string') {
            // Already an IPFS hash (string)
            ipfsHashes.push(img);
          } else if (img && img.file) {
            // Image object with file property - needs uploading
            imagesToUpload.push(img);
          }
        });
        
        // Upload new images if any
        if (imagesToUpload.length > 0) {
          const newHashes = await pinImagesToIPFS(
            imagesToUpload,
            process.env.REACT_APP_PINATA_JWT_2,
            process.env.REACT_APP_PINATA_GATEWAY_2
          );
          
          // Combine existing hashes with new ones
          const allHashes = [...ipfsHashes, ...newHashes];
          
          processedDraftData = {
            ...processedDraftData,
            metaDetails: {
              ...processedDraftData.metaDetails,
              images: allHashes
            }
          };
        } else {
          // No new images to upload, keep existing hashes
          processedDraftData = {
            ...processedDraftData,
            metaDetails: {
              ...processedDraftData.metaDetails,
              images: ipfsHashes
            }
          };
        }
      }
      
      // Save draft to backend
      const response = await saveDraft(
        walletAddress,
        propertyId || undefined,
        processedDraftData,
        currentStep
      );
      
      return response;
    } catch (error) {
      console.error('Error saving draft:', error);
      throw error;
    }
  }
  
  /**
   * Load a draft by property ID
   * @param {string} propertyId - Draft property ID
   * @returns {Promise<object>} Draft data
   */
  async loadDraft(propertyId) {
    try {
      const response = await getDraft(propertyId);
      
      if (response.success && response.draft) {
        return response.draft;
      } else {
        throw new Error('Draft not found');
      }
    } catch (error) {
      console.error('Error loading draft:', error);
      throw error;
    }
  }
  
  /**
   * Delete a draft
   * @param {string} propertyId - Draft property ID
   * @param {string} walletAddress - User's wallet address
   * @returns {Promise<object>} Delete response
   */
  async removeDraft(propertyId, walletAddress) {
    try {
      const response = await deleteDraft(propertyId, walletAddress);
      return response;
    } catch (error) {
      console.error('Error deleting draft:', error);
      throw error;
    }
  }
  
  /**
   * Get all drafts for a user with progress tracking
   * @param {string} walletAddress - User's wallet address
   * @param {boolean} includeFullData - Whether to include full draft data
   * @returns {Promise<object>} All drafts with summary
   */
  async getAllDrafts(walletAddress, includeFullData = true) {
    try {
      const response = await getAllDraftData(walletAddress, includeFullData);
      
      if (response.success) {
        return {
          drafts: response.drafts || [],
          summary: response.summary || {},
          total: response.total_drafts || 0
        };
      } else {
        throw new Error('Failed to fetch drafts');
      }
    } catch (error) {
      console.error('Error fetching all drafts:', error);
      throw error;
    }
  }
  
  /**
   * Auto-save draft with debouncing
   * @param {Function} saveFunction - The save function to execute
   * @param {number} delay - Delay in milliseconds
   * @returns {Function} Debounced save function
   */
  createAutoSave(saveFunction, delay = 2000) {
    let timeoutId;
    
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        saveFunction(...args);
      }, delay);
    };
  }
  
  /**
   * Parse draft data for minting form
   * @param {object} draft - Draft data from backend
   * @returns {object} Formatted data for minting form
   */
  parseDraftForForm(draft) {
    if (!draft || !draft.draftData) {
      return null;
    }
    
    const draftData = draft.draftData;
    
    return {
      metaDetails: draftData.metaDetails || {},
      short: draftData.short || {},
      images: draftData.metaDetails?.images || [],
      addressInput: draftData.metaDetails?.addressString || '',
      addressDetails: draftData.metaDetails?.address || null,
      pinLocation: draftData.metaDetails?.location || { lat: 25, lng: 55 },
      currentStep: draft.currentStep || 0,
      propertyId: draft.token_id || null,
    };
  }
}

// Export singleton instance
export default new DraftService();



